import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  LinearProgress,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import SpeedIcon from '@material-ui/icons/Speed';
import AccessibilityIcon from '@material-ui/icons/Accessibility';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import SearchIcon from '@material-ui/icons/Search';
import SmartphoneIcon from '@material-ui/icons/Smartphone';
import ComputerIcon from '@material-ui/icons/Computer';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Progress } from '@backstage/core-components';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import {
  fetchPageSpeedData,
  getScoreColor,
  getScoreStatus,
  type PageSpeedResult,
} from '../../services/pageSpeedService';

const useStyles = makeStyles(theme => ({
  scoreBox: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  scoreValue: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
  },
  scoreLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
  },
  metricItem: {
    padding: theme.spacing(1),
  },
  metricLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  metricValue: {
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: theme.spacing(1),
  },
  strategyButton: {
    marginRight: theme.spacing(1),
  },
  timestamp: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  errorBox: {
    marginTop: theme.spacing(2),
  },
}));

interface ScoreDisplayProps {
  score: number;
  label: string;
  icon: React.ReactNode;
}

const ScoreDisplay: React.FC<ScoreDisplayProps> = ({ score, label, icon }) => {
  const classes = useStyles();
  const color = getScoreColor(score);
  const status = getScoreStatus(score);

  const getColor = () => {
    switch (color) {
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'success':
        return '#4caf50';
      default:
        return '#999';
    }
  };

  return (
    <Box className={classes.scoreBox}>
      <Typography
        className={classes.scoreValue}
        style={{ color: getColor() }}
      >
        {score}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={score}
        className={classes.progressBar}
        style={{
          backgroundColor: '#e0e0e0',
        }}
        classes={{
          bar: color,
        }}
      />
      <Typography className={classes.scoreLabel}>
        {icon}
        {label}
      </Typography>
      <Chip
        label={status}
        size="small"
        style={{
          marginTop: 8,
          backgroundColor: getColor(),
          color: 'white',
          fontSize: '0.75rem',
        }}
      />
    </Box>
  );
};

export const EntityPageSpeedCard: React.FC = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PageSpeedResult | null>(null);
  const [strategy, setStrategy] = useState<'mobile' | 'desktop'>('desktop');

  // Get backend URL from config
  const backendUrl = config.getString('backend.baseUrl');

  // Try multiple annotation sources for website URL
  const websiteUrl = 
    entity.metadata.annotations?.['pagespeed/website-url'] ||
    (entity.spec as any)?.url as string | undefined;

  const getCacheKey = (url: string, strat: string) =>
    `pagespeed_${url}_${strat}`;

  const loadFromCache = (url: string, strat: string): PageSpeedResult | null => {
    try {
      const cached = localStorage.getItem(getCacheKey(url, strat));
      if (cached) {
        const parsed = JSON.parse(cached);
        const cacheTime = new Date(parsed.fetchTime);
        const now = new Date();
        // Cache for 1 hour
        if (now.getTime() - cacheTime.getTime() < 3600000) {
          return {
            ...parsed,
            fetchTime: new Date(parsed.fetchTime),
          };
        }
      }
    } catch (err) {
      // Ignore cache errors
    }
    return null;
  };

  const saveToCache = (url: string, strat: string, result: PageSpeedResult) => {
    try {
      localStorage.setItem(getCacheKey(url, strat), JSON.stringify(result));
    } catch (err) {
      // Ignore cache errors
    }
  };

  const loadData = async (forceRefresh = false) => {
    if (!websiteUrl) {
      setError('No website URL found. Please add pagespeed/website-url annotation.');
      return;
    }

    // Try to load from cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = loadFromCache(websiteUrl, strategy);
      if (cached) {
        setData(cached);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchPageSpeedData(websiteUrl, strategy, backendUrl);
      setData(result);
      saveToCache(websiteUrl, strategy, result);
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('429')) {
          setError('Rate limit exceeded. Please wait a few minutes before trying again. Results are cached for 1 hour.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to fetch PageSpeed data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load from cache on mount, don't auto-fetch
    if (websiteUrl) {
      const cached = loadFromCache(websiteUrl, strategy);
      if (cached) {
        setData(cached);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteUrl]);

  if (!websiteUrl) {
    return (
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <SpeedIcon style={{ marginRight: 8 }} />
              PageSpeed Insights
            </Box>
          }
        />
        <CardContent>
          <Alert severity="info">
            <Typography variant="body2" gutterBottom>
              <strong>Please add website URL to enable PageSpeed Insights</strong>
            </Typography>
            <Typography variant="body2" component="div">
              Add this annotation to your <code>catalog-info.yaml</code>:
            </Typography>
            <Box
              component="pre"
              style={{
                background: '#f5f5f5',
                padding: '8px',
                borderRadius: '4px',
                marginTop: '8px',
                fontSize: '12px',
              }}
            >
              {`metadata:
  annotations:
    pagespeed/website-url: https://your-website.com`}
            </Box>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const handleRefresh = () => {
    loadData(true); // Force refresh, ignore cache
  };

  const handleStrategyChange = (newStrategy: 'mobile' | 'desktop') => {
    setStrategy(newStrategy);
    // Check cache for new strategy
    if (websiteUrl) {
      const cached = loadFromCache(websiteUrl, newStrategy);
      if (cached) {
        setData(cached);
      } else {
        setData(null); // Clear data if no cache for this strategy
      }
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center">
            <SpeedIcon style={{ marginRight: 8 }} />
            PageSpeed Insights
          </Box>
        }
        action={
          <Box>
            <Tooltip title="Mobile">
              <IconButton
                size="small"
                className={classes.strategyButton}
                color={strategy === 'mobile' ? 'primary' : 'default'}
                onClick={() => handleStrategyChange('mobile')}
              >
                <SmartphoneIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Desktop">
              <IconButton
                size="small"
                className={classes.strategyButton}
                color={strategy === 'desktop' ? 'primary' : 'default'}
                onClick={() => handleStrategyChange('desktop')}
              >
                <ComputerIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Full Report">
              <IconButton
                size="small"
                component="a"
                href={`https://pagespeed.web.dev/analysis?url=${encodeURIComponent(websiteUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <CardContent>
        {!data && !loading && !error && (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Click <strong>Refresh</strong> to run PageSpeed analysis
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Results are cached for 1 hour to avoid API rate limits
            </Typography>
          </Box>
        )}

        {loading && <Progress />}

        {error && !loading && (
          <Alert severity="error" className={classes.errorBox}>
            {error}
          </Alert>
        )}

        {data && !loading && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <ScoreDisplay
                  score={data.scores.performance}
                  label="Performance"
                  icon={<SpeedIcon fontSize="small" />}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <ScoreDisplay
                  score={data.scores.accessibility}
                  label="Accessibility"
                  icon={<AccessibilityIcon fontSize="small" />}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <ScoreDisplay
                  score={data.scores.bestPractices}
                  label="Best Practices"
                  icon={<VerifiedUserIcon fontSize="small" />}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <ScoreDisplay
                  score={data.scores.seo}
                  label="SEO"
                  icon={<SearchIcon fontSize="small" />}
                />
              </Grid>
            </Grid>

            <Divider style={{ margin: '24px 0' }} />

            <Typography variant="h6" gutterBottom>
              Key Metrics
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={4}>
                <Box className={classes.metricItem}>
                  <Typography className={classes.metricLabel}>
                    First Contentful Paint
                  </Typography>
                  <Typography className={classes.metricValue}>
                    {data.metrics.firstContentfulPaint}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Box className={classes.metricItem}>
                  <Typography className={classes.metricLabel}>
                    Largest Contentful Paint
                  </Typography>
                  <Typography className={classes.metricValue}>
                    {data.metrics.largestContentfulPaint}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Box className={classes.metricItem}>
                  <Typography className={classes.metricLabel}>
                    Speed Index
                  </Typography>
                  <Typography className={classes.metricValue}>
                    {data.metrics.speedIndex}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Box className={classes.metricItem}>
                  <Typography className={classes.metricLabel}>
                    Time to Interactive
                  </Typography>
                  <Typography className={classes.metricValue}>
                    {data.metrics.timeToInteractive}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Box className={classes.metricItem}>
                  <Typography className={classes.metricLabel}>
                    Total Blocking Time
                  </Typography>
                  <Typography className={classes.metricValue}>
                    {data.metrics.totalBlockingTime}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Box className={classes.metricItem}>
                  <Typography className={classes.metricLabel}>
                    Cumulative Layout Shift
                  </Typography>
                  <Typography className={classes.metricValue}>
                    {data.metrics.cumulativeLayoutShift}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Typography className={classes.timestamp}>
              Last updated: {data.fetchTime.toLocaleString()}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

